import React from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { Icon, T, onboardingTheme, useOnboardingFonts } from './onboardingDesign'

/** The page around a Partner Onboarding screen: theme, fonts, background. */
export function OnboardingPage({ children, maxWidth = 1240, sx }) {
  useOnboardingFonts()
  return (
    <ThemeProvider theme={onboardingTheme}>
      <Box sx={{ background: T.pageBg, minHeight: '100vh', fontFamily: T.body, color: T.text, fontSize: 15, lineHeight: 1.5 }}>
        <Box sx={{ maxWidth, mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 4 }, ...sx }}>{children}</Box>
      </Box>
    </ThemeProvider>
  )
}

/** Eyebrow, display title, subtitle, and the page's actions on the right. */
export function PageHeader({ eyebrow = 'Partner onboarding', title, subtitle, actions, back, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      <Box sx={{ minWidth: 0 }}>
        {back && (
          <Button onClick={back.onClick} startIcon={<Icon.ArrowLeft size={16} />} sx={{ color: T.muted, px: 1, ml: -1, mb: 0.5, minHeight: 36, '&:hover': { color: T.text, background: T.fill } }}>
            {back.label}
          </Button>
        )}
        <Typography sx={{ color: T.redText, fontSize: 12.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', mb: 0.5 }}>{eyebrow}</Typography>
        <Typography component="h1" sx={{ fontFamily: T.display, fontWeight: 700, letterSpacing: '-0.02em', fontSize: { xs: 26, sm: 32 }, lineHeight: 1.15, color: T.text, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
        {subtitle && <Typography sx={{ color: T.text2, fontSize: 15, mt: 0.75 }}>{subtitle}</Typography>}
        {children}
      </Box>
      {actions && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</Box>}
    </Box>
  )
}

export function Card({ children, sx, ...rest }) {
  return (
    <Box sx={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '20px', p: { xs: 2, sm: 3 }, minWidth: 0, ...sx }} {...rest}>
      {children}
    </Box>
  )
}

/** A card's title, with an optional line under it and an action on the right. */
export function CardTitle({ children, sub, action, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 2, ...sx }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: 17, color: T.text, lineHeight: 1.35 }}>{children}</Typography>
        {sub && <Typography sx={{ color: T.hint, fontSize: 13.5, mt: 0.25, lineHeight: 1.5 }}>{sub}</Typography>}
      </Box>
      {action}
    </Box>
  )
}

const BADGE = {
  red: { background: T.redTint, color: T.redText },
  neutral: { background: T.neutralBadgeBg, color: T.neutralBadgeText },
  green: { background: T.greenTint, color: T.green },
  amber: { background: T.amberTint, color: T.amberText },
  purple: { background: T.purpleTint, color: T.purpleText },
  info: { background: T.infoBg, color: T.infoText },
}

export function Badge({ kind = 'neutral', children, sx }) {
  return (
    <Box component="span" sx={{ ...BADGE[kind], display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: 999, px: 1.25, py: 0.25, fontSize: 12.5, fontWeight: 700, lineHeight: 1.6, whiteSpace: 'nowrap', ...sx }}>
      {children}
    </Box>
  )
}

const STATUS = {
  DRAFT: ['Draft', 'neutral'],
  NEW: ['New', 'info'],
  CONTACTED: ['Contacted', 'amber'],
  SIGNED: ['Signed', 'purple'],
  ONBOARDED: ['Onboarded', 'green'],
  REJECTED: ['Rejected', 'red'],
}
export const statusLabel = s => (STATUS[s] ? STATUS[s][0] : s)

export function StatusBadge({ status, sx }) {
  const [label, kind] = STATUS[status] || [status, 'neutral']
  return <Badge kind={kind} sx={sx}>{label}</Badge>
}

const disabledLook = { background: T.disabledBg, color: T.disabledText }

/** The red pill. */
export function PrimaryButton({ children, busy, disabled, tone = 'red', sx, ...rest }) {
  const bg = tone === 'green' ? [T.green, '#245529'] : [T.red, T.redHover]
  return (
    <Button
      disabled={busy || disabled}
      sx={{ background: bg[0], color: '#fff', minHeight: 42, px: 2.5, gap: 0.75, '&:hover': { background: bg[1] }, '&.Mui-disabled': disabledLook, ...sx }}
      {...rest}
    >
      {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : children}
    </Button>
  )
}

/** Outlined red pill (secondary actions). */
export function OutlineButton({ children, busy, disabled, sx, ...rest }) {
  return (
    <Button
      disabled={busy || disabled}
      sx={{
        color: T.red, border: `1.5px solid ${T.red}`, background: '#fff', minHeight: 40, px: 2, gap: 0.75, whiteSpace: 'nowrap',
        '&:hover': { background: T.redTint, borderColor: T.redHover, color: T.redHover }, '&.Mui-disabled': { ...disabledLook, borderColor: T.disabledBg }, ...sx,
      }}
      {...rest}
    >
      {busy ? <CircularProgress size={16} sx={{ color: T.red }} /> : children}
    </Button>
  )
}

/** Transparent pill with a light border. */
export function GhostButton({ children, sx, ...rest }) {
  return (
    <Button sx={{ color: T.text, border: `1px solid ${T.input}`, background: 'transparent', minHeight: 40, px: 2, gap: 0.75, '&:hover': { background: T.fill, borderColor: T.inputHover }, ...sx }} {...rest}>
      {children}
    </Button>
  )
}

/** A text action in the link color. */
export function LinkButton({ children, sx, ...rest }) {
  return (
    <Button sx={{ color: T.link, px: 1, minHeight: 36, gap: 0.75, textAlign: 'left', '&:hover': { color: T.linkHover, background: T.redTint }, ...sx }} {...rest}>
      {children}
    </Button>
  )
}

export function StatCard({ value, label, tone = 'text' }) {
  const color = { text: T.text, red: T.red, green: T.green, purple: T.purpleText, info: T.infoText }[tone] || T.text
  return (
    <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: 32, lineHeight: 1.1, color, letterSpacing: '-0.02em' }}>{value}</Typography>
      <Typography sx={{ color: T.muted, fontSize: 14, mt: 0.5 }}>{label}</Typography>
    </Card>
  )
}

/** The persistent label above an input, as on the sign-up form. */
export function FieldLabel({ id, htmlFor, label, optional, sx }) {
  return (
    <Box component="label" id={id} htmlFor={htmlFor} sx={{ display: 'block', fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.35, mb: 0.75, ...sx }}>
      {label}
      {optional && <Box component="span" sx={{ fontWeight: 400, color: T.muted }}> (optional)</Box>}
    </Box>
  )
}

/** Label-over-value pairs, in one or two columns. */
export function KV({ rows, columns = 2 }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` }, columnGap: 2.5, rowGap: 1.75 }}>
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ minWidth: 0 }}>
          <Typography sx={{ color: T.hint, fontSize: 13, fontWeight: 600 }}>{label}</Typography>
          <Typography sx={{ color: T.text, fontSize: 14.5, overflowWrap: 'anywhere', mt: 0.25 }}>{value || '—'}</Typography>
        </Box>
      ))}
    </Box>
  )
}

const NOTICE_ICONS = { info: Icon.Info, success: Icon.Check, error: Icon.Alert, warning: Icon.Alert }

/** An Alert in the onboarding style, with a stroke icon. */
export function Notice({ severity = 'info', children, sx, ...rest }) {
  const I = NOTICE_ICONS[severity] || Icon.Info
  return <Alert severity={severity} icon={<I size={20} />} sx={{ mb: 2, ...sx }} {...rest}>{children}</Alert>
}

/** react-data-table-component styles to match. */
export const tableStyles = {
  table: { style: { fontFamily: T.body } },
  headRow: { style: { backgroundColor: T.footerBg, borderBottomColor: T.divider, minHeight: 48 } },
  headCells: { style: { fontWeight: 600, fontSize: 13, color: T.muted, fontFamily: T.body } },
  rows: {
    style: { fontFamily: T.body, fontSize: 14, color: T.text, minHeight: 64, borderBottomColor: `${T.divider} !important`, cursor: 'pointer' },
    highlightOnHoverStyle: { backgroundColor: T.footerBg, outline: 'none', borderBottomColor: T.divider },
  },
  pagination: { style: { fontFamily: T.body, color: T.muted, borderTopColor: T.divider, minHeight: 52 } },
}
